import type { SessionStorageAdapter } from '@erp/data-access/interfaces';
import type { IAppPreferences, ISecureStorage } from '@erp/native/interfaces';

/**
 * Wraps the native bridge in a Supabase-compatible storage adapter.
 *
 * **What we use, and why:**
 *
 * - Web: pass-through to localStorage via the web-adapter's secureStorage
 *   (which is just a thin wrapper). Same as Supabase's default.
 * - Desktop: pass-through to the **app preferences** store (Tauri's Store
 *   plugin), NOT Stronghold. Reasoning:
 *
 *   1. Supabase calls setItem + removeItem multiple times per signin.
 *      Stronghold's per-write cost is ~150-300ms (AEAD encrypt + disk
 *      write); Store is sub-10ms. Stronghold turned login into a 3-5s
 *      operation.
 *   2. Refresh tokens are a "session" credential — short-lived (30 days
 *      max), revocable from Supabase, scoped to one device. They don't
 *      warrant Stronghold-grade vault encryption. The OS protects the
 *      app-data dir per user.
 *   3. Stronghold stays available via `bridge.secureStorage` for higher-
 *      value secrets (caisse PIN codes, payment-card BINs if we ever add
 *      them) where the perf cost is acceptable.
 *
 * Supabase's `SupportedStorage` accepts async getItem/setItem/removeItem
 * (gotrue-js promisifies internally). Returning Promises here is fine.
 */
export function createSessionStorageBridge(
  preferences: IAppPreferences,
  fallbackSecure: ISecureStorage,
): SessionStorageAdapter {
  // Detect whether the bridge has a real preferences impl (desktop) or the
  // localStorage fallback (web). Both expose the same interface, but on
  // web we want to pass strings unchanged. Easiest check: try a probe set
  // and read it back — but that's overkill. Instead, both adapters JSON-
  // serialise the value, so if we always pass strings we get strings back.
  void fallbackSecure; // reserved for future selective use
  return {
    async getItem(key) {
      const v = await preferences.get<string>(key);
      return typeof v === 'string' ? v : null;
    },
    async setItem(key, value) {
      await preferences.set<string>(key, value);
    },
    async removeItem(key) {
      await preferences.remove(key);
    },
  };
}
